"use client";

import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import { useKYCDocuments } from "./_components/useKYCDocuments";
import KYCHeader from "./_components/KYCHeader";
import DocumentUploadCards from "./_components/DocumentUploadCards";
import AddDocumentForm from "./_components/AddDocumentForm";
import EditDocumentForm from "./_components/EditDocumentForm";
import DeleteDocumentModal from "./_components/DeleteDocumentModal";
import RoleRequirementNotice from "./_components/RoleRequirementNotice";
import { normalizeKycStatus } from "./_components/constants";

interface KYCPageDevOrchestratorProps {
  onClose?: () => void;
}

export function KYCPageDevOrchestrator({
  onClose,
}: KYCPageDevOrchestratorProps) {
  const kyc = useKYCDocuments({ onAllDocumentsUploaded: onClose });
  const { user, isRealtor, isDeveloper } = useAuth();
  const ph = usePostHogAnalytics();
  const role = isDeveloper ? "developer" : isRealtor ? "realtor" : "regular-user";

  const kycStatus = normalizeKycStatus(user?.kyc_status);
  const canShowUploadForms = !kyc.isVerified;

  // Funnel: KYC flow opened with upload intent. Once per mount.
  useEffect(() => {
    if (!kyc.isVerified) {
      ph.trackEvent("kyc_started", { role });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Funnel: back-office KYC verdict, captured when the dashboard renders it
  // to the user. Fires once per status change (localStorage guard).
  useEffect(() => {
    if (kycStatus !== "verified" && kycStatus !== "rejected") return;
    try {
      const key = "fnp_kyc_verdict_seen";
      if (window.localStorage.getItem(key) === kycStatus) return;
      ph.trackEvent(kycStatus === "verified" ? "kyc_verified" : "kyc_rejected", {
        role,
      });
      window.localStorage.setItem(key, kycStatus);
    } catch {
      /* storage unavailable — verdict event skipped */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycStatus]);

  return (
    <section
      aria-label="KYC verification"
      className="max-w-4xl mx-auto space-y-6 text-slate-700 font-sans p-2"
    >
      <KYCHeader kycStatus={kycStatus} onClose={onClose} />

      {!kyc.isVerified && (
        <RoleRequirementNotice
          isRealtor={isRealtor}
          isDeveloper={isDeveloper}
        />
      )}

      {!kyc.isAddFormOpen && !kyc.editingDocument && (
        <DocumentUploadCards
          documents={kyc.documents}
          stagedDocuments={kyc.stagedDocuments}
          isVerified={kyc.isVerified}
          isRealtor={isRealtor}
          isDeveloper={isDeveloper}
          onUploadType={kyc.startAddDocument}
          onEdit={kyc.startEdit}
          onDelete={kyc.setDocumentToDelete}
          onRemoveStaged={kyc.removeStagedDocument}
          onSubmit={() => kyc.submitMutation.mutate()}
          isSubmitPending={kyc.submitMutation.isPending}
        />
      )}

      {kyc.editingDocument && canShowUploadForms && (
        <EditDocumentForm
          documentTypeLabel={kyc.editingDocument.type.toUpperCase()}
          editNumber={kyc.editNumber}
          onEditNumberChange={kyc.handleEditNumberChange}
          firstName={kyc.editFirstName}
          onFirstNameChange={kyc.setEditFirstName}
          lastName={kyc.editLastName}
          onLastNameChange={kyc.setEditLastName}
          file={kyc.editFile}
          isUploading={kyc.isEditUploading}
          onFileChange={(file) => kyc.handleFile(file, true)}
          onRemoveFile={kyc.removeEditFile}
          isPending={kyc.editMutation.isPending}
          onCancel={kyc.cancelEdit}
          onSubmit={() => kyc.editMutation.mutate()}
        />
      )}

      {kyc.isAddFormOpen && canShowUploadForms && (
        <AddDocumentForm
          documentType={kyc.documentType}
          documentNumber={kyc.documentNumber}
          onDocumentNumberChange={kyc.handleDocumentNumberChange}
          firstName={kyc.firstName}
          onFirstNameChange={kyc.setFirstName}
          lastName={kyc.lastName}
          onLastNameChange={kyc.setLastName}
          file={kyc.file}
          isUploading={kyc.isUploading}
          uploadedFileUrl={kyc.uploadedFileUrl}
          onFileChange={(file) => kyc.handleFile(file, false)}
          onRemoveFile={kyc.removeFile}
          isPending={false}
          onCancel={kyc.cancelAddDocument}
          onSubmit={kyc.stageDocument}
        />
      )}

      <DeleteDocumentModal
        document={kyc.documentToDelete}
        isPending={kyc.deleteMutation.isPending}
        onConfirm={() =>
          kyc.documentToDelete &&
          kyc.deleteMutation.mutate(kyc.documentToDelete.id)
        }
        onClose={() => kyc.setDocumentToDelete(null)}
      />
    </section>
  );
}

export default KYCPageDevOrchestrator;
